import { Observable, of, Subject, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { DatesWithFlorderInfoTO } from 'app/core/store/calendar/common/calendar.interface';
import { MaterialTypes } from 'app/core/store/contract-details/contract-details.interface';
import { LoadingDatesTO } from 'app/core/store/edit-relocation-detail/relocation-detail.interface';
import { logger } from 'app/core/util/logger';
import { appSettings } from 'app/appSettings';
import {
  RelocationDetail,
  OrderDetailTO,
  isPalletMaterial, isPackingMaterial, isCombinationMaterial,
} from '../../store/edit-florder-detail/florder-detail.interface';
import { FlordersFilter } from '../../interfaces/florders-filter.interface';
import { FlordersTO } from '../../interfaces/florders.interface';
import { util } from '../../util/util';
import { default as cachedOrder } from './responses/getOrder';
import { default as cachedOrders } from './responses/getOrders';
import { default as loadingDates } from './responses/getRelocationLoadingDates';
import { default as materials } from './responses/getRelocationMaterials';
import { RestService } from './rest.service';
import { Customer } from '../../store/session/user-context/user-context.interface';
import { catchError } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RelocationRestService {
  constructor(protected restService: RestService) {}

  getRelocationsForCalendar(filter: FlordersFilter, customers: Customer[] = null): Observable<DatesWithFlorderInfoTO> {
    const params = this.getQueryParams({}, filter);
    return this.restService.get<DatesWithFlorderInfoTO>(`/Relocation/listRelocationNumbers?${params}`);
  }

  getRelocations(filter: FlordersFilter): Observable<FlordersTO> {
    if (appSettings.USE_MOCK_REST_DATA) {
      logger.log('MOCK REST getRelocations');
      return of(cachedOrders as FlordersTO);
    } else {
      if (filter != null) {
        const params = this.getQueryParams({}, filter);
        return this.restService.get<FlordersTO>(`/Relocation/query?${params}`);
      }
      return this.restService.get<FlordersTO>('/Relocation/query');
    }
  }

  getRelocation(etmOrderNr: string, salesOrderNumber: string): Observable<OrderDetailTO> {
    if (appSettings.USE_MOCK_REST_DATA) {
      logger.log('MOCK REST getRelocation');
      return of(cachedOrder as OrderDetailTO);
    } else {
      let url = `/Relocation/get?EtmOrderNumber=${etmOrderNr}`;
      if (salesOrderNumber) {
        url += `&SalesOrderNumber=${salesOrderNumber}`;
      }
      return this.restService.get<OrderDetailTO>(url);
    }
  }

  createRelocation(relocationDetail: RelocationDetail): Observable<RelocationDetail> {
    const postData = this.createPostData(relocationDetail);
    return this.restService.post<RelocationDetail>('/Relocation/create', postData);
  }

  getLoadingDates({ fromId, toId }: { fromId: string; toId: string }): Observable<LoadingDatesTO> {
    if (appSettings.USE_MOCK_REST_DATA) {
      logger.log('MOCK REST getLoadingDates');
      const subject = new Subject<LoadingDatesTO>();
      setTimeout(() => {
        subject.next(loadingDates);
        subject.complete();
      }, 200);
      return subject.asObservable();
    } else {
      return this.restService.get<LoadingDatesTO>(`/Relocation/getLoadingDates?FromId=${fromId}&ToId=${toId}&NumberOfMonths=6`).pipe(
        catchError(result => {
          return throwError(() => result.error);
        })
      );
    }
  }

  getMaterials(depotId: string): Observable<MaterialTypes> {
    if (appSettings.USE_MOCK_REST_DATA) {
      logger.log('MOCK REST getMaterials');
      const subject = new Subject<MaterialTypes>();
      setTimeout(() => {
        subject.next(materials);
        subject.complete();
      }, 200);
      return subject.asObservable();
    } else {
      return this.restService.get<MaterialTypes>(`/Relocation/getMaterials?DepotId=${depotId}`).pipe(
        catchError(result => {
          return throwError(() => ({
            status: result.status,
            result,
          }));
        })
      );
    }
  }

  /**
   * RFC - 1312
   * Business wants to show batches that are known for both In & Out depot
   * This translates in us (delaware) sending a fromId & toId + no longer sending DepotId
   * This function is a replica of the getMaterials function, due to make sure nothing breaks (regression)
   * + getMaterials is used multiple times this way we are 100% sure
   * @param fromId string
   * @param toId string
   * @returns Observable<MaterialTypes>
   */
  getMaterialsWithFromToId(fromId: string, toId: string): Observable<MaterialTypes> {
    return this.restService.get<MaterialTypes>(`/Relocation/getMaterials?FromId=${fromId}&ToId=${toId}`).pipe(
      catchError(result => {
        return throwError(() => ({
          status: result.status,
          result,
        }));
      })
    );
  }

  protected getQueryParams(params: any, filter: FlordersFilter) {
    const newParamsObject = {
      ...params,
      OrderType: filter.orderTypes,
      Transporter: filter.transporter,
      OrderDateFrom: filter.florderDateFrom,
      OrderDateTo: filter.florderDateTo,
      UnloadingDateFrom: filter.unloadingDateFrom,
      UnloadingDateTo: filter.unloadingDateTo,
      PalletId: filter.palletIds,
      From: filter.froms,
      To: filter.tos,
      CustomerRefNumber: filter.customerReferenceNumber || null,
      SenderRefNumber: filter.senderReferenceNumber || null,
      SalesOrderNumber: filter.salesOrderNumber || null,
      ShipmentNumber: filter.etmShippingNumber || null,
      EtmOrderNumber: filter.etmDocumentNumber || null,
      PageNumber: filter.pageNr,
      PageSize: filter.pageSize,
      SortingColumn: filter.sortField,
      SortingType: filter.sortAscending ? 'asc' : 'desc',
      OrderStatus: filter.orderStatuses,
    };
    return util.serializeObjectToParams(newParamsObject);
  }

  protected createPostData(relocationDetail: RelocationDetail): any {
    const postData = {
      orderType: relocationDetail.deliveryMethod.type,
      fromId: relocationDetail.deliveryMethod.from.id,
      toId: relocationDetail.deliveryMethod.to.id,
      requestedUnloadingDates: relocationDetail.planning.unloadingDate,
      transport: 'Z1',
      globalType: '',
      transporter: relocationDetail.deliveryMethod.transporter,
      licensePlate: relocationDetail.deliveryMethod.licensePlate,
      customerRefNumber: relocationDetail.deliveryMethod.customerReferenceNumber,
      senderRefNumber: relocationDetail.deliveryMethod.senderReferenceNumber,
      exchangePallets: relocationDetail.planning.exchangePallets || false,
      comments: util.cleanValue(relocationDetail.deliveryMethod.remarks),
      numberExchangePallets: relocationDetail.planning.exchangePallets ? relocationDetail.planning.numberOfExchangePallets || 0 : 0,
      lineItem: relocationDetail.materials.map(m => {
        return {
          lineType: m.type,
          palletId: m.type !== 'packing' ? util.cleanValue(m.palletId) : undefined,
          packingId: !isPalletMaterial(m) ? util.cleanValue(m.packingId) : undefined,
          packingStatus: util.cleanValue(m.packingStatus),
          logisticsVarietyPacking: util.cleanValue(m.logisticsVarietyPacking),
          packingsPerPallet: isCombinationMaterial(m) ? util.cleanValue(m.packingsPerPallet) : undefined,
          numberOfPallets: !isPackingMaterial(m) ? util.cleanValue(m.numberOfPallets) : undefined,
          packingQuantity: !isPalletMaterial(m) ? util.cleanValue(m.packingQuantity) || util.toNumber(m['packingsPerPallet']) * util.toNumber(m['numberOfPallets']) : undefined,
        };
      }),
    };
    if (relocationDetail.planning.loadingDate) {
      postData['requestedLoadingDate'] = relocationDetail.planning.loadingDate;
    }
    return postData;
  }
}
